/**
 * useKeyboardSpacerFollow
 *
 * Drives an in-flow keyboard spacer (a block at the bottom of a page column) per frame so a
 * composer pinned above it rides the iOS keyboard exactly, with no CSS transition and no React
 * render on the path. The ClientSheet keyboard follow, applied to a height instead of a lift.
 *
 * Why not a transitioned CSS variable (phone 2026-10-03, chat composer): WKWebView gives no
 * per-frame keyboard signal; Telegram's `viewportChanged` and `visualViewport.resize` land as
 * one discrete event at or after the end of the ~250 ms native animation, and Telegram shrinks
 * the webview (`window.innerHeight`) later still. A transition from a predicted number lands
 * short or long of the real keyboard, and a composer sitting at the very bottom of the screen
 * is then partly under the keyboard the instant WebKit checks whether to reveal the focused
 * field. WebKit scrolls the whole webview to reveal it, `usePreventNativeKeyboardScroll` snaps
 * it back, and the admin sees the header and the thread jump up and down at settle.
 *
 * Here every frame reads LIVE geometry and writes the spacer the same frame:
 *   layoutBottom  = the scope element's bottom on screen (the page column lives in the admin
 *                   shell, whose height is LOCKED to the largest stable viewport, so the column's
 *                   bottom stays put while Telegram shrinks the webview; `window.innerHeight`
 *                   would read the shrunk webview and put the overlap at zero, dropping the
 *                   composer behind the keyboard)
 *   visibleBottom = min(window.innerHeight, visualViewport.offsetTop + height, Telegram viewportHeight)
 *   overlap       = layoutBottom - visibleBottom
 * OPEN: the spacer rises on the iOS curve toward the keyboard's height; the target is the larger of
 * the prediction (seeded from the last measured keyboard, else a per-device estimate) and the live
 * overlap, so Telegram's early report of the real height (it lands ~86 ms in, while the keyboard is
 * still animating; phone trace 2026-10-03) corrects the target without jumping the bar;
 * post-curve while geometry is still exactly the focus-time snapshot the prediction HOLDS (a
 * stale zero would drop the composer behind the keyboard); once geometry has moved the loop
 * tracks live until it settles. CLOSE: the descent is predicted in visible-bottom space so it
 * animates even when the webview anchor was holding the composer up. Hardware keyboard (no
 * viewport change within the no-show window): the spacer runs back down.
 */
import { useEffect, type RefObject } from 'react';
import { rememberKeyboardPx, rememberedKeyboardPx } from './useKeyboardAwareScroll';
import { useViewportSource } from './viewportSource';

/** Keyboard open/close animation window (ms): the iOS keyboard curve driven in JS. */
const ANIM_MS = 250;
/** Keep the loop alive at least this long so the late real viewport event is still cancelled. */
const SETTLE_GUARD_MS = 450;
/** Below this per-frame delta (px) the height counts as stable. */
const SETTLE_EPS = 0.5;
/** Geometry equal within this (px) counts as unchanged when detecting the real keyboard event. */
const DEVIATION_EPS = 1;
/** No viewport change within this window after focus: no on-screen keyboard appeared. */
const NO_SHOW_MS = 700;

function makeCubicBezier(p1x: number, p1y: number, p2x: number, p2y: number): (t: number) => number {
  const sampleX = (t: number) => 3 * p1x * t * (1 - t) ** 2 + 3 * p2x * t ** 2 * (1 - t) + t ** 3;
  const sampleY = (t: number) => 3 * p1y * t * (1 - t) ** 2 + 3 * p2y * t ** 2 * (1 - t) + t ** 3;
  const slopeX = (t: number) => 3 * p1x * (1 - t) ** 2 + 6 * (p2x - p1x) * t * (1 - t) + 3 * (1 - p2x) * t ** 2;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 6; i += 1) {
      const slope = slopeX(t);
      if (slope === 0) break;
      t -= (sampleX(t) - x) / slope;
    }
    return sampleY(Math.min(1, Math.max(0, t)));
  };
}
/** Approximate iOS keyboard open/close curve. */
const EASE = makeCubicBezier(0.17, 0.59, 0.4, 0.77);

/**
 * First-focus estimate when nothing has been measured this session: the portrait iOS keyboard
 * with its suggestion bar, by device class. A short estimate puts the composer under the keyboard
 * at the reveal check; a slightly long one leaves a sliver of page above the keyboard until live
 * geometry corrects it, which is the harmless side.
 */
export function deviceKeyboardEstimatePx(): number {
  const w = window.screen?.width ?? 390;
  const h = window.screen?.height ?? 844;
  if (h < 700) return 260; // iPhone SE class
  if (w >= 414) return 346; // Plus and Pro Max class
  return 336; // 6.1 inch class
}

const isFormEl = (el: EventTarget | null): el is HTMLElement =>
  el instanceof HTMLElement &&
  (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.getAttribute('contenteditable') === 'true');

export interface KeyboardSpacerFollowOptions {
  /**
   * Extra room above the keyboard while it is up. iOS reveals the focused field after its
   * keyboard animation when the field sits closer to the keyboard than it likes, scrolling the
   * whole web view (phone trace 2026-10-03: a 19px window scroll at the moment Telegram shrank
   * the web view, snapped back by the app's guard). Lifting the composer this much further
   * leaves iOS nothing to reveal.
   */
  extraPx?: number;
}

export function useKeyboardSpacerFollow(
  scopeRef: RefObject<HTMLElement | null>,
  spacerRef: RefObject<HTMLElement | null>,
  options: KeyboardSpacerFollowOptions = {},
): void {
  const { extraPx = 0 } = options;
  // Telegram's viewport inside the Mini App, none on the website (viewportSource.ts).
  const getViewport = useViewportSource();
  useEffect(() => {
    const webApp = getViewport();
    const predictiveEnabled = (navigator.maxTouchPoints ?? 0) > 0;

    const readLive = () => {
      const scopeBottom = scopeRef.current?.getBoundingClientRect().bottom ?? 0;
      const layoutBottom = scopeBottom > 0 ? scopeBottom : window.innerHeight;
      const vv = window.visualViewport;
      let visibleBottom = Math.min(window.innerHeight, vv ? vv.offsetTop + vv.height : window.innerHeight);
      const tg = webApp?.viewportHeight;
      if (typeof tg === 'number' && tg > 0) visibleBottom = Math.min(visibleBottom, tg);
      return { layoutBottom, visibleBottom };
    };
    const readMeasuredKeyboard = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--app-keyboard-height');
      const n = Number.parseFloat(raw);
      return Number.isFinite(n) && n > 0 ? n : 0;
    };

    let raf = 0;
    let phase: 'open' | 'close' | null = null;
    let phaseStart = 0;
    let predictedKb = 0;
    let currentHeight = 0;
    let settleFrames = 0;
    let focused = false;
    let resetTimer: number | undefined;
    let startLayoutBottom = 0;
    let startVisibleBottom = 0;
    let liveLatched = false;
    let closeStartVB = 0;
    let closeEndVB = 0;

    const changed = (a: number, b: number) => Math.abs(a - b) > DEVIATION_EPS;
    const write = (px: number) => {
      currentHeight = px;
      const spacer = spacerRef.current;
      if (spacer) spacer.style.height = `${px}px`;
    };
    // The extra room rides on top of any keyboard height, never with the keyboard down.
    const withExtra = (overlap: number) => (overlap > 0 ? overlap + extraPx : 0);
    const stopLoop = () => {
      if (raf) {
        window.cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    const compute = (now: number) => {
      const elapsed = now - phaseStart;
      const t = Math.min(1, elapsed / ANIM_MS);
      const { layoutBottom, visibleBottom } = readLive();
      if (phase === 'close') {
        const desiredVB = closeStartVB + EASE(t) * (closeEndVB - closeStartVB);
        return { height: Math.max(0, layoutBottom - desiredVB), t, elapsed, noShow: false };
      }
      if (!liveLatched && (changed(layoutBottom, startLayoutBottom) || changed(visibleBottom, startVisibleBottom))) {
        liveLatched = true;
      }
      const live = Math.max(0, layoutBottom - visibleBottom);
      let height: number;
      if (t < 1) {
        // The curve leads the rise toward the best-known height: the prediction, or the live
        // overlap once Telegram has reported it. Reality corrects the target, never the easing.
        height = EASE(t) * withExtra(Math.max(predictedKb, live));
      } else if (liveLatched) {
        height = withExtra(live);
      } else {
        height = withExtra(predictedKb); // geometry still the focus-time snapshot: provably stale, hold
      }
      return { height: Math.max(0, height), t, elapsed, noShow: !liveLatched && t >= 1 };
    };

    const beginClose = (now: number) => {
      phase = 'close';
      phaseStart = now;
      const { layoutBottom } = readLive();
      closeStartVB = layoutBottom - currentHeight;
      closeEndVB = startLayoutBottom || layoutBottom;
      settleFrames = 0;
    };

    const frame = (now: number) => {
      const r = compute(now);
      const moved = Math.abs(r.height - currentHeight);
      write(r.height);
      if (phase === 'close') {
        if (r.t >= 1 && (r.height < SETTLE_EPS || r.elapsed >= SETTLE_GUARD_MS)) {
          write(0);
          stopLoop();
          return;
        }
      } else if (r.noShow && r.elapsed >= NO_SHOW_MS) {
        beginClose(now);
      } else if (liveLatched && r.elapsed >= SETTLE_GUARD_MS && moved < SETTLE_EPS) {
        if (++settleFrames >= 2) {
          if (r.height > extraPx) rememberKeyboardPx(r.height - extraPx);
          stopLoop();
          return;
        }
      } else {
        settleFrames = 0;
      }
      raf = window.requestAnimationFrame(frame);
    };
    const startLoop = () => {
      if (!raf) raf = window.requestAnimationFrame(frame);
    };
    const kickLive = () => {
      if (!focused || !predictiveEnabled || raf) return;
      phase = 'open';
      phaseStart = performance.now() - ANIM_MS;
      liveLatched = true;
      settleFrames = 0;
      startLoop();
    };

    const onFocusIn = (e: FocusEvent) => {
      const scope = scopeRef.current;
      const el = e.target;
      if (!scope || !isFormEl(el) || !scope.contains(el)) return;
      if (resetTimer) {
        clearTimeout(resetTimer);
        resetTimer = undefined;
      }
      if (focused) return; // refocus after a send: the keyboard stays up, keep the height
      focused = true;
      if (!predictiveEnabled) return;
      predictedKb = readMeasuredKeyboard() || rememberedKeyboardPx() || deviceKeyboardEstimatePx();
      const snap = readLive();
      startLayoutBottom = snap.layoutBottom;
      startVisibleBottom = snap.visibleBottom;
      // Focused with the keyboard already up (geometry already shrunk): track live from the start,
      // never mistake the unchanged snapshot for a keyboard that failed to appear.
      liveLatched = snap.layoutBottom - snap.visibleBottom > DEVIATION_EPS;
      phase = 'open';
      phaseStart = performance.now();
      settleFrames = 0;
      startLoop();
    };
    const onFocusOut = (e: FocusEvent) => {
      const scope = scopeRef.current;
      const next = e.relatedTarget;
      if (scope && isFormEl(next) && scope.contains(next)) return;
      if (resetTimer) clearTimeout(resetTimer);
      // WKWebView can report a null relatedTarget on a refocus; confirm focus really left.
      resetTimer = window.setTimeout(() => {
        resetTimer = undefined;
        const active = document.activeElement;
        if (scope && isFormEl(active) && scope.contains(active)) return;
        focused = false;
        if (!predictiveEnabled) return;
        beginClose(performance.now());
        startLoop();
      }, 60);
    };
    const onViewport = () => {
      if (raf) write(compute(performance.now()).height); // correct synchronously, idempotent with the next frame
      else kickLive();
    };

    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('focusout', onFocusOut, true);
    const vv = window.visualViewport;
    vv?.addEventListener('resize', onViewport);
    vv?.addEventListener('scroll', onViewport);
    window.addEventListener('resize', onViewport);
    webApp?.onEvent('viewportChanged', onViewport);

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('focusout', onFocusOut, true);
      vv?.removeEventListener('resize', onViewport);
      vv?.removeEventListener('scroll', onViewport);
      window.removeEventListener('resize', onViewport);
      webApp?.offEvent('viewportChanged', onViewport);
      if (resetTimer) clearTimeout(resetTimer);
      stopLoop();
      write(0);
    };
  }, [extraPx, getViewport, scopeRef, spacerRef]);
}
